import { useCallback, useEffect, useRef, useState } from "react";
import type { AuditFilterState, AuditPage, AuditRow } from "./types";
import { EMPTY_FILTERS } from "./types";

interface UseAuditQueryOpts {
  /**
   * Fetch one page of audit rows. Called with the current filters and
   * (optionally) a cursor from the previous page. Must resolve to the
   * server response shape.
   */
  fetchPage: (filters: AuditFilterState, cursor: string | null) => Promise<AuditPage>;
  /** Called once on mount unless overridden. */
  initialFilters?: Partial<AuditFilterState>;
  /**
   * If falsy, the hook stays idle (useful while org context is still
   * warming up on the shell side).
   */
  enabled?: boolean;
}

interface UseAuditQueryResult {
  filters: AuditFilterState;
  setFilters: (next: AuditFilterState) => void;
  rows: AuditRow[];
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refresh: () => void;
}

export function useAuditQuery({
  fetchPage,
  initialFilters,
  enabled = true,
}: UseAuditQueryOpts): UseAuditQueryResult {
  const [filters, setFiltersState] = useState<AuditFilterState>({
    ...EMPTY_FILTERS,
    ...initialFilters,
  });
  const [rows, setRows] = useState<AuditRow[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Bump this to force a fresh fetch (clear cursor, clear rows).
  const [reloadTick, setReloadTick] = useState(0);

  // Guard against out-of-order responses when filters change mid-request.
  const requestIdRef = useRef(0);

  const runFirstPage = useCallback(async () => {
    if (!enabled) return;
    const id = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    try {
      const page = await fetchPage(filters, null);
      if (id !== requestIdRef.current) return;
      setRows(page.logs);
      setCursor(page.nextCursor);
      setHasMore(!!page.nextCursor);
    } catch (err) {
      if (id !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load activity");
      setRows([]);
      setCursor(null);
      setHasMore(false);
    } finally {
      if (id === requestIdRef.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchPage, filters, enabled, reloadTick]);

  useEffect(() => {
    runFirstPage();
  }, [runFirstPage]);

  const loadMore = useCallback(async () => {
    if (!enabled || !cursor || loadingMore) return;
    const id = requestIdRef.current;
    setLoadingMore(true);
    try {
      const page = await fetchPage(filters, cursor);
      // Guard against filter changes during pagination.
      if (id !== requestIdRef.current) return;
      setRows((prev) => [...prev, ...page.logs]);
      setCursor(page.nextCursor);
      setHasMore(!!page.nextCursor);
    } catch (err) {
      if (id !== requestIdRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load more activity");
    } finally {
      if (id === requestIdRef.current) setLoadingMore(false);
    }
  }, [enabled, cursor, loadingMore, fetchPage, filters]);

  return {
    filters,
    setFilters: setFiltersState,
    rows,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refresh: () => setReloadTick((n) => n + 1),
  };
}
